import {
  CustomQueryBuilder,
  type GetCheckoutPurchaseIntentsByPurchaseIntentIdResponse,
  type GetCheckoutPurchaseIntentsResponse,
  queryKeys,
  useSessionToken,
} from "@birdwatch/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useQueryState } from "@/hooks/use-query-state";

const MULTIPLE_FOUND = "Multiple purchase intents found";
const NOT_FOUND = "Purchase intent not found";

const listBuilder = () =>
  new CustomQueryBuilder<GetCheckoutPurchaseIntentsResponse>(
    "checkout",
    "/purchase-intents",
  );

const detailBuilder = (purchaseIntentId: string) =>
  new CustomQueryBuilder<GetCheckoutPurchaseIntentsByPurchaseIntentIdResponse>(
    "checkout",
    "/purchase-intents/:purchaseIntentId",
    { purchaseIntentId },
  );

/**
 * Cache key of the current checkout's intent. It sits under the detail route's
 * key, so a `purchaseIntent.changed` refresh of that intent reaches it; the
 * trailing slot keeps it apart from the generated detail hook, which caches the
 * response envelope rather than the bare intent this query holds. An empty id
 * is the "single pending intent on the account" read.
 */
export function currentPurchaseIntentQueryKey(purchaseIntentId: string) {
  return [
    ...queryKeys.getCheckoutPurchaseIntentsByPurchaseIntentId({
      purchaseIntentId,
    }),
    "current",
  ];
}

/**
 * Resolves the purchase intent for the current checkout: the one named in the
 * URL, or — when the URL has none — the single pending intent on the account.
 *
 * Two chained requests inside one query, so this drives the builders directly
 * rather than composing two `useGet…()` hooks (the second id is only known
 * after the first response).
 */
function resolveCurrentIntent(purchaseIntentId: string, token: string | null) {
  return async () => {
    let currentId = purchaseIntentId;

    if (!currentId) {
      // The endpoint serves a limit/offset page — `{ items, total, limit,
      // offset }` — not the canonical list envelope. The generated type
      // matches (platform: checkout/purchase-intent-list.dto.ts).
      const list = await listBuilder().fetch(token);
      const items = list.data?.items ?? [];
      const pending = items.filter((item) => !item.stripeInvoiceId);
      if (pending.length > 1) throw new Error(MULTIPLE_FOUND);
      currentId = items[0]?.id ?? "";
    }

    if (!currentId) throw new Error(NOT_FOUND);

    const detail = await detailBuilder(currentId).fetch(token);
    return detail.data;
  };
}

/**
 * @param options.enabled - Gate the request. A caller that knows it has no intent yet
 *   passes `false` so the list→detail fallback below never runs: that fallback resolves
 *   the account's single pending intent, which for a funnel with its own intent id would
 *   mean reading — and minting a Stripe SetupIntent against — someone else's checkout.
 */
export const useCurrentPurchaseIntent = (options?: { enabled?: boolean }) => {
  const [purchaseIntentIdRaw] = useQueryState<string>("purchaseIntentId");
  const purchaseIntentId = purchaseIntentIdRaw ?? "";
  const getToken = useSessionToken();

  return useQuery({
    queryKey: currentPurchaseIntentQueryKey(purchaseIntentId),
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const token = await getToken();
      return resolveCurrentIntent(purchaseIntentId, token)();
    },
    retry: (failureCount, error) => {
      // Both are terminal answers about the account's state, not transients.
      if (error.message === MULTIPLE_FOUND || error.message === NOT_FOUND) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export const usePrefetchCurrentPurchaseIntent = () => {
  const queryClient = useQueryClient();
  const getToken = useSessionToken();

  return async (purchaseIntentId = "") => {
    const queryKey = currentPurchaseIntentQueryKey(purchaseIntentId);
    if (queryClient.getQueryState(queryKey)?.status === "success") return;

    const token = await getToken();
    await queryClient.prefetchQuery({
      queryKey,
      queryFn: resolveCurrentIntent(purchaseIntentId, token),
    });
  };
};
